/**
 * DocumentsTab — study documents, read from the platform registry (eReg).
 *
 * P3 (source-of-truth plan §5.3): Site Organizer no longer uploads into Site
 * Intelligence — the study's documents live in eReg and render via the shared
 * StudyDocumentsCard (slots, versions, extraction state, View). Uploads are
 * eReg deep links that return here. Protocol-scoped files uploaded to
 * Intelligence before P3 remain visible read-only below until they're adopted
 * into eReg (Intelligence → Registry adoption).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FileText, Upload, Download } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { StudyDocumentsCard } from '../../ui';
import { eregStudyUploadLink } from '../../ui/returnTo';
import type { IntelligenceDocument } from '../../intelligence/types';

interface DocumentsTabProps {
  protocolId: string;
  onDocumentCountChange?: (count: number) => void;
}

/** Legacy Intelligence-owned rows only — projection/adopted rows are eReg's. */
const isLegacyRow = (doc: IntelligenceDocument) => !doc.registry_document_id;

export function DocumentsTab({ protocolId, onDocumentCountChange }: DocumentsTabProps) {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = ['super_admin', 'administrator'].includes(user?.role || '');
  const returnTo = location.pathname;

  const [legacyDocs, setLegacyDocs] = useState<IntelligenceDocument[]>([]);
  const [registryCount, setRegistryCount] = useState(0);
  const [downloading, setDownloading] = useState<string | null>(null);

  const loadCounts = useCallback(async () => {
    try {
      const [registryRes, legacyRes] = await Promise.all([
        api.get(`/api/registry/studies/${protocolId}/documents`).catch(() => ({ data: { documents: [] } })),
        // Legacy list needs Intelligence access — hide the section gracefully without it.
        api.get(`/api/intelligence/protocols/${protocolId}/documents`).catch(() => ({ data: [] })),
      ]);
      const legacy = ((legacyRes.data || []) as IntelligenceDocument[]).filter(isLegacyRow);
      // Match what the card shows — archived registry docs are hidden there.
      const registryDocs = (registryRes.data?.documents || []).filter((d: any) => d.status !== 'archived');
      setRegistryCount(registryDocs.length);
      setLegacyDocs(legacy);
    } catch (error) {
      console.error('Error loading document counts:', error);
    }
  }, [protocolId]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    onDocumentCountChange?.(registryCount + legacyDocs.length);
  }, [registryCount, legacyDocs.length, onDocumentCountChange]);

  const handleDownloadLegacy = async (doc: IntelligenceDocument) => {
    setDownloading(doc.id);
    try {
      const response = await api.get(`/api/intelligence/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={eregStudyUploadLink(protocolId, { role: 'protocol', returnTo, returnLabel: 'Site Organizer' })}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
          >
            <Upload className="h-4 w-4" /> Upload protocol in eReg
          </Link>
          <Link
            to={eregStudyUploadLink(protocolId, { role: 'icf', returnTo, returnLabel: 'Site Organizer' })}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            <Upload className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Upload ICF in eReg
          </Link>
        </div>
      )}

      <StudyDocumentsCard
        protocolId={protocolId}
        canManageInEreg={isAdmin}
        returnTo={returnTo}
        returnLabel="Site Organizer"
      />

      {legacyDocs.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Legacy uploads</h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Files uploaded to Site Intelligence before documents moved to eReg. New uploads and
            versions go to eReg above.
          </p>
          <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
            {legacyDocs.map(doc => (
              <li key={doc.id} className="flex flex-wrap items-center gap-3 py-3 sm:flex-nowrap">
                <FileText className="h-5 w-5 flex-shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {doc.title || doc.filename}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {doc.document_type || 'document'}
                    {doc.version ? <> · v{doc.version}</> : null}
                    {doc.created_at ? <> · {new Date(doc.created_at).toLocaleDateString()}</> : null}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDownloadLegacy(doc)}
                  disabled={downloading === doc.id}
                  aria-label={`Download ${doc.filename}`}
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:min-h-0 sm:min-w-0"
                >
                  <Download className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default DocumentsTab;
